import Decimal from 'decimal.js'
import isEqual from 'lodash/isEqual'
import React, { useContext, useEffect, useId, useRef, useState } from 'react'
import { useAppSelector } from '../../../app/hooks'
import { SessionContext } from '../../../providers/session/SessionContext'
import { getSettings, validate } from '../../../services/ValidationService'
import { assignReferences, calcuRiskGainValues, extractExtraFieldProperties, noop, RunOnceEffect } from '../../../utils/Utils'
import { fieldService } from '../../FieldService'
import { ChildReference, FieldProperties, FormMode, MoneyDTO, OddsType } from '../../types'
import FieldLabel from '../field-label/FieldLabel'
import LoadingSpinner from '../loading-spinner/LoadingSpinner'
import { GuslFormState } from '../maintain-form/guslFormSlice'
import { OddsDTO } from '../odds/types'
import { FieldContentStyled, FieldContentWrapperStyled, FloatingFormStyled } from '../text/styled'
import {
    AmountContainerStyled,
    ButtonsContainerStyled,
    RadioButtonContainerStyled,
    RadioButtonContentStyled,
    RadioButtonIconStyled,
    RadioButtonLabelStyled,
} from './styled'
import { RiskGainRadioBO, RiskGainRequestDO } from './types'

const CALC_URL = '/sportsbookbet/get-risk-gain'

export const RiskGainRadioField = (properties: FieldProperties): React.ReactElement<FieldProperties> => {
    const uniqueId = useId() // properties?.uniqueId ||
    const sessionContext = useContext(SessionContext)
    const [keyRisk, setKeyRisk] = useState<any>('risk-')
    const [keyGain, setKeyGain] = useState<any>('gain-')

    const _guslFormState: GuslFormState = useAppSelector((state) => state.guslFormSlice[properties.code])

    const [formMode, setFormMode] = useState(properties.formMode)
    const [formValue, setFormValue] = useState<RiskGainRadioBO | undefined>(
        _guslFormState?.getFieldValue(properties) || properties.data || {}
    )
    const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined)
    const [submitted, setSubmitted] = useState(false)
    const valueRef = useRef(properties?.data)
    const [loading, setLoading] = useState(false)

    const extraFieldProperties = extractExtraFieldProperties(properties)
    const [childReferences] = useState<Map<string, ChildReference>>(new Map())

    const [selectedButton, setSelectedButton] = useState<string>(
        _guslFormState?.getFieldValue(properties)?.isGain || false ? 'gain' : 'risk'
    )
    const latestOddsRef = React.useRef<any>(null)

    const riskFieldConfig = {
        name: 'risk',
        label: 'Risk',
        type: 'money',
        extraFieldProperties: extraFieldProperties,
    }
    const gainFieldConfig = {
        name: 'gain',
        label: 'Gain',
        type: 'money',
        extraFieldProperties: extraFieldProperties,
    }

    const onFormModeChange = (mode: FormMode) => {
        setFormMode(mode)
        setFormValue(properties?.data)
    }

    useEffect(() => {
        setFormValue(properties?.data)
    }, [properties])

    useEffect(() => {
        const risk = _guslFormState?.getFieldValue(properties)?.risk as MoneyDTO
        const data = {
            odds: latestOddsRef.current,
            risk: risk,
            gain: _guslFormState?.getFieldValue(properties)?.gain as MoneyDTO,
            useGain: risk?.value && risk?.value !== 0 ? false : true,
        }
        calcRiskGain(data)
    }, [latestOddsRef.current])

    useEffect(() => {
        const currentRisk = _guslFormState?.getFieldValue(properties)?.risk as MoneyDTO
        const currentGain = _guslFormState?.getFieldValue(properties)?.gain as MoneyDTO
        const newCurrency = _guslFormState?.getValueForField('counterparty')?.currencyCode || currentRisk?.currency?.code

        // Update currency for both risk and gain
        const updatedRisk = {
            ...currentRisk,
            currency: {
                ...currentRisk?.currency,
                code: newCurrency,
            },
            value: currentRisk?.value ?? 0,
        }

        const updatedGain = {
            ...currentGain,
            currency: {
                ...currentGain?.currency,
                code: newCurrency,
            },
            value: currentGain?.value ?? 0,
        }

        const data = {
            odds: latestOddsRef.current,
            risk: updatedRisk,
            gain: updatedGain,
            useGain: currentRisk?.value && currentRisk?.value !== 0 ? false : true,
        }
        calcRiskGain(data)
    }, [_guslFormState?.getValueForField('counterparty')?.currencyCode])

    const doValidation = (fieldValue: any): boolean => {
        setSubmitted(true)
        const message = validate(
            properties.menuItem?.code,
            properties.fieldConfig.name,
            properties.fieldConfig.validators,
            'checkbox',
            fieldValue
        )
        if (message) {
            setErrorMessage(message)
            return false
        }
        return true
    }

    RunOnceEffect(() => {
        assignReferences(properties.reference, onFormModeChange, noop, doValidation)
    })

    const onSubmitted = (): void => {
        console.log('-- onSubmitted')
    }

    const calcRiskGain = (data: RiskGainRequestDO) => {
        if (!data.odds || data?.odds?.value === undefined) {
            return
        }

        setLoading(true)
        const response = calcuRiskGainValues(
            {
                value: new Decimal(data.odds.value),
                type: data.odds.type as OddsType, // Ensure that odds.type is a valid OddsType enum value.
            },
            {
                value: new Decimal(data?.risk?.value || 0),
                currency: data?.risk?.currency ?? { code: 'USD' },
            },
            {
                value: new Decimal(data?.gain?.value || 0),
                currency: data?.gain?.currency ?? { code: 'USD' },
            },
            data.useGain
        )
        if (data.useGain) {
            setKeyRisk(new Date().getTime())
        } else {
            setKeyGain(new Date().getTime())
        }

        const latest = {
            risk: response?.risk,
            gain: response?.gain,
            useGain: data.useGain,
        }

        setFormValue(latest)
        properties.onChange(properties.fieldConfig.name, latest)

        setLoading(false)
    }

    const calcRiskGainOld = (data: RiskGainRequestDO) => {
        if (!data.odds || data?.odds?.value === undefined) {
            return
        }
        setLoading(true)
        sessionContext
            .post<RiskGainRequestDO, RiskGainRequestDO>(CALC_URL, {
                odds: data?.odds,
                risk: data?.risk,
                gain: data?.gain,
                useGain: data.useGain,
            } as RiskGainRequestDO)
            .then((response) => {
                if (data.useGain) {
                    setKeyRisk(new Date().getTime())
                } else {
                    setKeyGain(new Date().getTime())
                }

                const latest = {
                    risk: response.data.risk,
                    gain: response.data.gain,
                    useGain: data.useGain,
                }
                setFormValue(latest)
                properties.onChange(properties.fieldConfig.name, latest)

                setLoading(false)
            })
            .catch((error) => {
                setLoading(false)
                console.error('Error in getting header', error)
            })
    }

    const renderTableView = (): React.ReactElement => {
        return (
            <>
                {selectedButton === 'risk' && fieldService.getTableField(riskFieldConfig, properties.menuItem, formValue)?.render()}
                {selectedButton === 'gain' && fieldService.getTableField(gainFieldConfig, properties.menuItem, formValue)?.render()}
            </>
        )
    }

    const onRadioClick = (e: React.MouseEvent<HTMLElement, MouseEvent>, type: string) => {
        setSelectedButton(type)
        const data = {
            odds: _guslFormState?.getValueForField('odds'),
            risk: _guslFormState?.getFieldValue(properties)?.risk as MoneyDTO,
            gain: _guslFormState?.getFieldValue(properties)?.gain as MoneyDTO,
            useGain: selectedButton === 'gain',
        }

        calcRiskGain(data)
    }

    const renderRadioButton = (label: string, type: string): React.ReactElement => {
        return (
            <RadioButtonContainerStyled>
                <RadioButtonIconStyled
                    active={selectedButton === type}
                    onClick={(e: React.MouseEvent<HTMLElement, MouseEvent>) => onRadioClick(e, type)}
                />
                <RadioButtonLabelStyled onClick={(e: React.MouseEvent<HTMLElement, MouseEvent>) => onRadioClick(e, type)}>
                    {label}
                </RadioButtonLabelStyled>
            </RadioButtonContainerStyled>
        )
    }

    const getCurrentRowData = () => {
        return properties.data
    }
    const registerChildReference = (reference: ChildReference) => {
        childReferences.set(reference.name, reference)
    }

    const onChangeHandler = (name: string, value: any, oddsValue: any): void => {
        const gain = name === 'gain' ? value : formValue?.gain
        const risk = name === 'risk' ? value : formValue?.risk
        const latest = {
            risk: risk,
            gain: gain,
            useGain: name === 'gain',
        }
        setFormValue(latest)
        properties.onChange(properties.fieldConfig.name, latest)
        const data = {
            odds: oddsValue as OddsDTO,
            risk: risk as MoneyDTO,
            gain: gain as MoneyDTO,
            useGain: name === 'gain',
        }

        calcRiskGain(data)
    }

    const renderMoneyEntry = (): React.ReactElement => {
        const fieldData = formValue ? formValue[selectedButton === 'risk' ? 'risk' : 'gain'] : undefined
        const fieldConfig = selectedButton === 'risk' ? riskFieldConfig : gainFieldConfig
        return (
            <AmountContainerStyled id={'amt_' + selectedButton} key={'amt_' + selectedButton}>
                {fieldService.getFormTemplate({
                    code: properties.code,
                    formMode: formMode,
                    fieldConfig,
                    // menuItem: properties.menuItem,
                    data: fieldData,
                    onSubmitted,
                    onChange: (name: string, value: any) => onChangeHandler(name, value, undefined),
                    getCurrentRowData: getCurrentRowData,
                    isDialog: false,
                    menuItem: undefined,
                    reference: {
                        name: fieldConfig.name,
                        displayOrder: 1,
                        register: registerChildReference,
                    },
                    reLoad: properties.reLoad,
                    inline: true,
                })}
            </AmountContainerStyled>
        )
    }

    const renderMoneyEntryField = ({ field }: { field: string }): React.ReactElement => {
        let fieldData = formValue ? formValue[field === 'risk' ? 'risk' : 'gain'] : undefined
        let fieldConfig = field === 'risk' ? riskFieldConfig : gainFieldConfig

        const currency = _guslFormState?.getValueForField('counterparty')?.currencyCode || fieldData?.currency?.code

        if (!isEqual(latestOddsRef.current, _guslFormState?.getValueForField('odds'))) {
            latestOddsRef.current = _guslFormState?.getValueForField('odds')
        }

        let newKey = field
        if (currency && currency !== fieldData?.currency?.code) {
            newKey += `-${currency}`
        }

        fieldData = {
            ...fieldData,
            currency: {
                ...fieldData?.currency,
                code: currency,
            },
            value: fieldData?.value ?? 0,
        }

        return (
            <AmountContainerStyled id={'amt_' + field} key={'amt_' + newKey}>
                {fieldService.getFormTemplate({
                    code: properties.code,
                    formMode: formMode,
                    fieldConfig,
                    data: fieldData, // Use the updated fieldData
                    onSubmitted,
                    onChange: (name: string, value: any) => {
                        onChangeHandler(name, value, latestOddsRef.current)
                    },
                    getCurrentRowData: getCurrentRowData,
                    isDialog: false,
                    menuItem: undefined,
                    reference: {
                        name: fieldConfig.name,
                        displayOrder: 1,
                        register: registerChildReference,
                    },
                    reLoad: properties.reLoad,
                    inline: true,
                })}
            </AmountContainerStyled>
        )
    }

    const renderRiskGain = (): React.ReactElement => {
        const editable = formMode === FormMode.NEW || formMode === FormMode.EDIT
        if (!editable) {
            return renderTableView()
        }

        return (
            <ButtonsContainerStyled>
                <RadioButtonContainerStyled>
                    {renderRadioButton('Risk', 'risk')}
                    {renderRadioButton('Gain', 'gain')}
                </RadioButtonContainerStyled>
                <RadioButtonContentStyled>
                    {loading && <LoadingSpinner />}
                    {!loading && renderMoneyEntry()}
                </RadioButtonContentStyled>
            </ButtonsContainerStyled>
        )
    }

    const renderRiskGainLabels = (): React.ReactElement => {
        const editable = formMode === FormMode.NEW || formMode === FormMode.EDIT
        if (!editable) {
            return renderTableView()
        }

        const fieldConfig = selectedButton === 'risk' ? riskFieldConfig : gainFieldConfig

        return (
            <FloatingFormStyled>
                <FieldContentWrapperStyled>
                    <FieldContentStyled id={'fcs_' + 'risk'} formMode={formMode} key={keyRisk}>
                        <FieldLabel properties={properties} label="Risk" />
                        {renderMoneyEntryField({ field: 'risk' })}
                        {submitted && errorMessage && <small className="yellow">{errorMessage}</small>}
                    </FieldContentStyled>
                    <FieldContentStyled id={'fcs_' + 'gain'} formMode={formMode} key={keyGain}>
                        <FieldLabel properties={properties} label="Gain" />
                        {renderMoneyEntryField({ field: 'gain' })}
                        {submitted && errorMessage && <small className="yellow">{errorMessage}</small>}
                    </FieldContentStyled>
                    {submitted && errorMessage && <small className="yellow">{errorMessage}</small>}
                </FieldContentWrapperStyled>
            </FloatingFormStyled>
        )
    }

    const renderFormView = (): React.ReactElement => {
        const onDataInputChange = (value: string) => {}
        const [hideField, disableField, required] = getSettings(formMode, properties.fieldConfig, formValue)

        if (!hideField && properties.inline) {
            return <></>
        }

        return (
            <>
                {!hideField && (
                    <>
                        <FloatingFormStyled>
                            <FieldContentWrapperStyled>
                                <FieldContentStyled formMode={formMode}>{renderRiskGainLabels()}</FieldContentStyled>
                                {submitted && errorMessage && <small className="yellow">{errorMessage}</small>}
                            </FieldContentWrapperStyled>
                            <FieldLabel properties={properties} />
                        </FloatingFormStyled>
                    </>
                )}
            </>
        )
    }

    return <>{properties.isTableView ? renderTableView() : renderFormView()}</>
}
